import React, { useState, useEffect } from "react";
import { getClubDetail } from "../../services/clubService";
import { useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../../components/child/Alert/SuccessAlert";
import DengerAlert from "../../components/child/Alert/DengerAlert";

// Helper to render fee - supports object { monday: 100, tuesday: 120, ... } or simple value
const renderFee = (value, prefix = "$") => {
  if (value == null || value === "") return "N/A";
  if (typeof value === "object" && !Array.isArray(value)) {
    const dayPrices = Object.entries(value).map(([day, price]) => (
      <span key={day} className="d-block mb-1">
        {day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()}: {prefix}{price}
      </span>
    ));
    return dayPrices.length ? dayPrices : "N/A";
  }
  return `${prefix}${value}`;
};

const formatDayLabel = (day) =>
  day ? day.charAt(0).toUpperCase() + day.slice(1).toLowerCase() : "N/A";

const getListingType = (startDate, endDate) => {
  if (!startDate || !endDate) return "N/A";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  return sameDay ? "One Time" : "Repeat";
};

const renderBottleServiceCategory = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return <p>No data found</p>;

  return list.map((drink, index) => (
    <div key={`${drink?.name || "drink"}-${index}`} className="col-md-12 mb-3 border rounded p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <strong>{drink?.name || "N/A"}</strong>
        {drink?.image ? (
          <img
            src={drink.image}
            alt={drink?.name || "Bottle"}
            className="img-fluid rounded"
            style={{ width: "64px", height: "64px", objectFit: "cover" }}
          />
        ) : null}
      </div>

      {drink?.dayWise && Object.keys(drink.dayWise).length > 0 ? (
        <div className="table-responsive">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>Day</th>
                <th>Quantity</th>
                <th>Reservation Fee</th>
                <th>Bottle Price</th>
                <th>Available</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(drink.dayWise).map(([day, dayData]) => (
                <tr key={`${drink?.name || "drink"}-${day}`}>
                  <td>{formatDayLabel(day)}</td>
                  <td>{dayData?.quantity ?? "N/A"}</td>
                  <td>{renderFee(dayData?.reservationFee, "$")}</td>
                  <td>{renderFee(dayData?.bottlePrice, "$")}</td>
                  <td>{dayData?.isAvailable ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mb-0">No day-wise data</p>
      )}
    </div>
  ));
};

const ViewClubPageNew = () => {
  const [items, setItems] = useState({});
  const [activeTab, setActiveTab] = useState("view-club");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getClubDetail(id);
      console.log(response);
      setItems(response.data.data || {});
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to fetch club detail");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  return (
    <div className="row gy-4">
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            {/* Tab Navigation */}
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="club-view-tab"
              role="tablist"
            >
              {/* Club Profile always */}
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "view-club" ? "active" : ""
                  }`}
                  id="tab-view-club"
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("view-club")}
                >
                  Club Profile
                </button>
              </li>

              {/* Conditionally render Booth tab */}
              {items.booths && items.booths.length > 0 && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${
                      activeTab === "view-booth" ? "active" : ""
                    }`}
                    id="tab-view-booth"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-booth")}
                  >
                    Booth Detail
                  </button>
                </li>
              )}

              {/* Conditionally render Ticket tab */}
              {items.tickets && items.tickets.length > 0 && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${
                      activeTab === "view-ticket" ? "active" : ""
                    }`}
                    id="tab-view-ticket"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-ticket")}
                  >
                    Ticket Detail
                  </button>
                </li>
              )}
            </ul>

            <div className="tab-content" id="club-tab-content">
              {/* Club Profile */}
              <div
                className={`tab-pane fade ${
                  activeTab === "view-club" ? "show active" : ""
                }`}
                id="tab-content-view-club"
                role="tabpanel"
              >
                {/* Club Details */}
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Name:
                    </label>
                    {items?.name || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Detail:
                    </label>
                    {items?.details || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Type:
                    </label>
                    {items?.type || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Entry Fee:
                    </label>
                    {renderFee(items?.entryFee, "$")}
                  </div>
                  {/* <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Open Time:
                    </label>
                    {items?.openingTime || "N/A"}
                  </div> */}
                  {/* <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Close Time:
                    </label>
                    {items?.closeTime || "N/A"}
                  </div> */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Address:
                    </label>
                    {items?.address || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Status:
                    </label>
                    {items?.status || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Listing Type:
                    </label>
                    {getListingType(items?.startDate, items?.endDate)}
                  </div>
                  <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Start Date :
                        </label>
                        {items?.startDate
                          ? new Date(items.startDate)
                              .toLocaleDateString("en-GB")
                              .replace(/\//g, "-")
                          : "N/A"}
                      </div>
                   <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          End Date:
                        </label>
                        {items?.endDate
                          ? new Date(items.endDate)
                              .toLocaleDateString("en-GB")
                              .replace(/\//g, "-")
                          : "N/A"}
                      </div>
                  {items?.type != "Table" && (
                     <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Capacity:
                    </label>
                    {items?.eventCapacity || "N/A"}
                  </div>
                  )}
                 
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Gratuity:
                    </label>
                    {items?.isGratuity === true || items?.isGratuity === "true"
                      ? "Yes"
                      : "No"}
                  </div>
                   <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Gratuity Value(%):
                    </label>
                    {items?.gratuityValue || "N/A"}
                  </div>
                  {/* Table Type Details */}
                  {items?.type === "Table" && (
                    <>
                      {/* <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Table Number:
                        </label>
                        {items?.tableNumber || "N/A"}
                      </div> */}

                      {/* <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Table Detail:
                        </label>
                        {items?.details || "N/A"}
                      </div> */}

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Minimum Guest:
                        </label>
                        {items?.minGuest || "N/A"}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Maximum Guest:
                        </label>
                        {items?.maxGuest || "N/A"}
                      </div>

                      {/* Floor Layout */}
                      {items?.floorLayout && (
                        <div className="row">
                          <label className="form-label fw-bold me-2 mb-0">
                            Floor Layout:
                          </label>

                          <div className="col-md-3 mb-3">
                            <img
                              src={items.floorLayout}
                              alt="Floor Layout"
                              className="img-fluid rounded"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {items?.type === "Table" && (
                    <>
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Table Type:
                        </label>
                        {items?.tableType || "N/A"}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Table Capacity:
                        </label>
                        {items?.tableCapacity || "N/A"}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Female Price:
                        </label>
                        {renderFee(items?.femalePrice, "$")}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Male Price:
                        </label>
                        {renderFee(items?.malePrice, "$")}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Booking:
                        </label>
                        {items?.enDisTableBooking
                          ? "Enabled"
                          : "Disabled" || "N/A"}
                      </div>
                      
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Open Time:
                        </label>
                        {items?.openingTime || "N/A"}
                      </div>
                     
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Close Time:
                        </label>
                        {items?.closeTime || "N/A"}
                      </div>
                    </>
                  )}

                  {/* Facilities */}
                  {items.facilities && items.facilities.length > 0 && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold mb-1">
                        Facilities:
                      </label>

                      {items.facilities
                        .map((fc) => fc?.name || "N/A")
                        .join(", ")}
                    </div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold mb-1">
                    Booking Accepted:
                  </label>
                    {items?.bookingAccepted || "N/A"}
                </div>
                {items?.type === "BottleService" && (
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12 mb-2">
                        <label className="form-label fw-bold mb-1">
                          Bottle Service: {items?.bottleService?.enabled ? "Enabled" : "Disabled"}
                        </label>
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="form-label fw-bold mb-1">
                          Show Bottle Prices:
                        </label>{" "}
                        {items?.bottleService?.showBottlePrices ? "Yes" : "No"}
                      </div>

                      <div className="col-md-12 mb-2">
                        <h6 className="mb-2">Alcoholic</h6>
                        <div className="row">
                          {renderBottleServiceCategory(items?.bottleService?.alcoholic)}
                        </div>
                      </div>

                      <div className="col-md-12 mb-2">
                        <h6 className="mb-2">Non Alcoholic</h6>
                        <div className="row">
                          {renderBottleServiceCategory(items?.bottleService?.nonAlcoholic)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Photos */}
                {items.photos && items.photos.length > 0 && (
                  <div className="row">
                    <label className="form-label fw-bold me-2 mb-0">
                      Photos:
                    </label>
                    {items.photos.map((photo, index) => (
                      <div key={index} className="col-md-3 mb-3">
                        <img
                          src={photo}
                          alt={`Photo ${index + 1}`}
                          className="img-fluid rounded"
                          style={{ width: "100px", height: "100px" }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Photos */}
              </div>

              {/* Booth Detail */}
              <div
                className={`tab-pane fade ${
                  activeTab === "view-booth" ? "show active" : ""
                }`}
                id="tab-content-view-booth"
                role="tabpanel"
              >
                {items.booths && items.booths.length > 0 ? (
                  <div className="row">
                    {items.booths.map((booth, index) => (
                      <div key={index} className="col-md-12 mb-4">
                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold me-2 mb-0">
                              Booth Name:
                            </label>
                            {booth?.boothName || "N/A"}
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold me-2 mb-0">
                              Booth Type:
                            </label>
                            {booth?.boothTypeName || "N/A"}
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold me-2 mb-0">
                              Capacity:
                            </label>
                            {booth?.capacity || "N/A"}
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold me-2 mb-0">
                              Booth Price:
                            </label>
                            {renderFee(booth?.boothPrice, "$")}
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold me-2 mb-0">
                              Booth Discounted Price:
                            </label>
                            {renderFee(booth?.discountedPrice, "$")}
                          </div>
                        </div>

                        {/* Booth Images */}
                        {booth?.boothImage && booth.boothImage.length > 0 && (
                          <div className="row mt-2 border-bottom">
                            {booth.boothImage.map((photo, i) => (
                              <div key={i} className="col-md-2 mb-3">
                                <img
                                  src={photo}
                                  alt={`Booth ${index + 1} - Photo ${i + 1}`}
                                  className="img-fluid rounded"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No data found</p>
                )}
              </div>

              {/* Tickets */}
              <div
                className={`tab-pane fade ${
                  activeTab === "view-ticket" ? "show active" : ""
                }`}
                id="tab-content-view-ticket"
                role="tabpanel"
              >
                {items.tickets && items.tickets.length > 0 ? (
                  <div className="row">
                    {items.tickets.map((ticket, index) => (
                      <div key={index} className="col-md-12 mb-3 border-bottom">
                        <div className="row">
                          <div className="col-md-4 mb-2">
                            <label className="form-label fw-bold me-2 mb-0">
                              Ticket Type:
                            </label>
                            {ticket?.ticketTypeName || "N/A"}
                          </div>
                          <div className="col-md-4 mb-2">
                            <label className="form-label fw-bold me-2 mb-0">
                              Ticket Price:
                            </label>
                            {renderFee(ticket?.ticketPrice, "$")}
                          </div>
                          <div className="col-md-4 mb-2">
                            <label className="form-label fw-bold me-2 mb-0">
                              Capacity:
                            </label>
                            {ticket?.capacity || "N/A"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No data found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewClubPageNew;
